export type StateLeaseLaw = {
  slug: string;
  state: string;
  abbr: string;
  redFlagCount: number;
  laws: {
    title: string;
    summary: string;
    statute?: string;
  }[];
  comparison: {
    topic: string;
    law: string;
    commonClause: string;
  }[];
  faqs: { q: string; a: string }[];
  relatedArticles: { title: string; href: string }[];
};

const currentYear = new Date().getFullYear();
export const PSEO_YEAR = currentYear;

const baseRelated = [
  { title: "10 Illegal Rental Clauses to Watch For", href: "/blog/lease-red-flags" },
  { title: "How to Read a Lease Like a Lawyer", href: "/blog" },
  { title: "Lease Decoder: Free AI Lease Analyzer", href: "/tools/lease-decoder" },
];

export const stateLeaseLaws: Record<string, StateLeaseLaw> = {
  california: {
    slug: "california",
    state: "California",
    abbr: "CA",
    redFlagCount: 7,
    laws: [
      {
        title: "Security deposit cap",
        summary:
          "As of July 2024, landlords can collect no more than one month's rent as a security deposit (Civ. Code §1950.5).",
        statute: "Cal. Civ. Code §1950.5",
      },
      {
        title: "Notice to enter",
        summary:
          "Landlords must give at least 24 hours' written notice before entering, except in emergencies.",
        statute: "Cal. Civ. Code §1954",
      },
      {
        title: "Habitability & repairs",
        summary:
          "Landlords must keep units habitable. Tenants may repair-and-deduct up to one month's rent if the landlord fails to act within a reasonable time.",
        statute: "Cal. Civ. Code §1942",
      },
      {
        title: "Rent increase limits",
        summary:
          "Statewide rent cap (AB 1482) limits annual increases to 5% + local CPI, capped at 10% total, on most units 15+ years old.",
        statute: "Cal. Civ. Code §1947.12",
      },
      {
        title: "Just-cause eviction",
        summary:
          "After 12 months of tenancy, landlords need a legally recognized 'just cause' to terminate.",
      },
      {
        title: "Late-fee reasonableness",
        summary:
          "Late fees must be a reasonable estimate of damages. Courts routinely strike fees above 6% of monthly rent.",
      },
      {
        title: "Mandatory disclosures",
        summary:
          "Lead paint, mold, bed bugs, flood zone, and demolition permits must be disclosed in writing.",
      },
    ],
    comparison: [
      {
        topic: "Security deposit",
        law: "Max 1 month's rent (2024)",
        commonClause: "'2 months' rent + last month upfront'",
      },
      {
        topic: "Entry notice",
        law: "24 hours written",
        commonClause: "'Landlord may enter at any time'",
      },
      {
        topic: "Repairs",
        law: "Repair-and-deduct allowed",
        commonClause: "'Tenant waives right to repair-and-deduct'",
      },
      { topic: "Late fee", law: "Reasonable (~6%)", commonClause: "'$100/day late fee'" },
      {
        topic: "Attorney's fees",
        law: "Mutual under Civ. Code §1717",
        commonClause: "'Tenant pays landlord's attorney fees'",
      },
    ],
    faqs: [
      {
        q: "Can my California landlord charge more than one month's deposit?",
        a: "Generally no. Since July 2024, security deposits are capped at one month's rent, with a narrow exception for small landlords (max two units) renting to non-service-member tenants.",
      },
      {
        q: "Is a 'no pets, no exceptions' clause enforceable in California?",
        a: "It's enforceable for pets, but assistance animals (service and emotional support animals) are not pets under the FEHA — they must be accommodated.",
      },
      {
        q: "Can I be evicted without a reason?",
        a: "Not after 12 months of tenancy. AB 1482 requires landlords to state a 'just cause' such as nonpayment, lease violation, or qualifying no-fault reason.",
      },
      {
        q: "Are jury-trial waivers in my lease valid?",
        a: "No. California courts have repeatedly struck pre-dispute jury waivers in residential leases as unenforceable.",
      },
      {
        q: "What if my landlord won't fix something?",
        a: "Document the issue in writing. If it isn't fixed in a reasonable time (often 30 days), you may repair-and-deduct up to one month's rent, or in serious cases withhold rent.",
      },
    ],
    relatedArticles: baseRelated,
  },
  "new-york": {
    slug: "new-york",
    state: "New York",
    abbr: "NY",
    redFlagCount: 6,
    laws: [
      {
        title: "Security deposit cap",
        summary:
          "Capped at one month's rent under the 2019 Housing Stability and Tenant Protection Act.",
        statute: "NY Gen. Oblig. Law §7-108",
      },
      {
        title: "Application fees",
        summary: "Background and credit-check fees are capped at $20 statewide.",
      },
      { title: "Late fees", summary: "Capped at $50 or 5% of monthly rent, whichever is less." },
      {
        title: "Rent increase notice",
        summary: "90 days' notice required for increases over 5% on tenants of 2+ years.",
      },
      {
        title: "Warranty of habitability",
        summary: "Cannot be waived. Applies to every residential lease in NY.",
      },
      {
        title: "Right to organize",
        summary:
          "Tenants have a statutory right to form a tenants' association without retaliation.",
      },
    ],
    comparison: [
      {
        topic: "Security deposit",
        law: "Max 1 month",
        commonClause: "'First, last, and 2-month deposit'",
      },
      {
        topic: "Application fee",
        law: "Max $20",
        commonClause: "'$100 non-refundable application fee'",
      },
      { topic: "Late fee", law: "Max $50 or 5%", commonClause: "'10% of monthly rent'" },
      {
        topic: "Habitability",
        law: "Cannot be waived",
        commonClause: "'Tenant accepts unit as-is'",
      },
      {
        topic: "Rent increase notice",
        law: "30–90 days",
        commonClause: "'Rent may change with 7 days notice'",
      },
    ],
    faqs: [
      {
        q: "How much can my NY landlord charge for a deposit?",
        a: "One month's rent. Anything more violates the 2019 HSTPA.",
      },
      {
        q: "Are broker fees still legal in NYC?",
        a: "As of 2025, the FARE Act shifts most broker fees to the party that hired the broker — usually the landlord.",
      },
      {
        q: "Can my landlord refuse to renew without reason?",
        a: "In rent-stabilized units, no. In market-rate units, they generally can — but must give proper notice (30–90 days based on tenancy length).",
      },
      {
        q: "What's the warranty of habitability?",
        a: "An automatic guarantee that your unit is fit to live in. It cannot be waived in any lease clause.",
      },
      {
        q: "Are 'no guests overnight' clauses valid?",
        a: "No. NY's Roommate Law allows you to have one additional occupant plus their dependent children, regardless of lease language.",
      },
    ],
    relatedArticles: baseRelated,
  },
  texas: {
    slug: "texas",
    state: "Texas",
    abbr: "TX",
    redFlagCount: 6,
    laws: [
      {
        title: "Security deposit return",
        summary: "Must be returned within 30 days of move-out with itemized deductions.",
        statute: "Tex. Prop. Code §92.103",
      },
      {
        title: "Repair obligations",
        summary:
          "Landlords must repair conditions affecting health and safety after written notice.",
      },
      {
        title: "Lockout protection",
        summary: "Landlords cannot lock tenants out except under narrow statutory conditions.",
      },
      {
        title: "Late fee reasonableness",
        summary:
          "Late fees must be a reasonable estimate of damages — capped at 12% (small properties) or 10% otherwise.",
      },
      {
        title: "Right to terminate (family violence)",
        summary: "Survivors of family violence can terminate the lease early with documentation.",
      },
      {
        title: "Mandatory smoke alarm provision",
        summary: "Landlords must install and maintain working smoke detectors — non-waivable.",
      },
    ],
    comparison: [
      {
        topic: "Deposit return",
        law: "30 days + itemized",
        commonClause: "'Deposit non-refundable'",
      },
      {
        topic: "Lockouts",
        law: "Strictly limited",
        commonClause: "'Landlord may change locks if late'",
      },
      { topic: "Late fee", law: "Max 10–12%", commonClause: "'$50/day late fee'" },
      {
        topic: "Repairs",
        law: "Required after notice",
        commonClause: "'Tenant responsible for all repairs'",
      },
      {
        topic: "Early termination (DV)",
        law: "Allowed by statute",
        commonClause: "'No early termination for any reason'",
      },
    ],
    faqs: [
      {
        q: "How long does my Texas landlord have to return my deposit?",
        a: "30 days from the date you surrender the property and provide a forwarding address.",
      },
      {
        q: "Can my landlord change the locks if I'm late on rent?",
        a: "Only under strict statutory conditions and only once per rental period — and they must give you a key on request.",
      },
      {
        q: "What's a reasonable late fee in Texas?",
        a: "Up to 12% of rent for properties with 4 or fewer units; 10% for larger properties.",
      },
      {
        q: "Can I break my lease for family violence?",
        a: "Yes. Tex. Prop. Code §92.016 allows early termination with proper documentation.",
      },
      {
        q: "Are smoke detector waivers enforceable?",
        a: "No. The landlord's duty to install and maintain smoke alarms cannot be waived.",
      },
    ],
    relatedArticles: baseRelated,
  },
  florida: {
    slug: "florida",
    state: "Florida",
    abbr: "FL",
    redFlagCount: 5,
    laws: [
      {
        title: "Deposit handling",
        summary:
          "Deposits must be held in a separate FL bank account or be bonded, with written notice within 30 days.",
      },
      {
        title: "Notice to enter",
        summary: "12 hours' notice required, and only at reasonable times.",
      },
      {
        title: "Termination notice",
        summary: "Month-to-month leases require 30 days' notice (recent change from 15 days).",
      },
      {
        title: "Repair & habitability",
        summary:
          "Landlords must comply with building/housing codes; tenants can withhold rent after 7-day notice.",
      },
      {
        title: "Retaliation prohibited",
        summary: "Landlords can't retaliate for code complaints or organizing.",
      },
    ],
    comparison: [
      {
        topic: "Deposit",
        law: "Separate account + notice",
        commonClause: "'Deposit commingled with operating funds'",
      },
      { topic: "Entry notice", law: "12 hours", commonClause: "'Landlord may enter anytime'" },
      { topic: "M2M termination", law: "30 days", commonClause: "'7-day notice to vacate'" },
      {
        topic: "Repairs",
        law: "7-day cure rule",
        commonClause: "'Tenant waives right to withhold rent'",
      },
      { topic: "Retaliation", law: "Prohibited", commonClause: "'Landlord may terminate at will'" },
    ],
    faqs: [
      {
        q: "How must my Florida landlord hold my deposit?",
        a: "In a separate non-interest-bearing FL bank account, an interest-bearing account, or via a surety bond — and they must notify you in writing within 30 days.",
      },
      {
        q: "How much notice to enter is required?",
        a: "At least 12 hours, and only at reasonable times (generally 7:30am–8pm).",
      },
      {
        q: "Can my landlord evict me for complaining to code enforcement?",
        a: "No. Florida law explicitly prohibits retaliatory eviction.",
      },
      {
        q: "How much notice to end a month-to-month tenancy?",
        a: "30 days, following a 2024 statutory change from 15 days.",
      },
      {
        q: "Can I withhold rent for repairs?",
        a: "Yes, after delivering proper 7-day written notice and if the landlord fails to fix the condition.",
      },
    ],
    relatedArticles: baseRelated,
  },
};

export const stateSlugs = Object.keys(stateLeaseLaws);
